import Icon from '@/components/ui/AppIcon';

interface ComparisonItem {
  feature: string;
  silkSolutions: string | boolean;
  traditional: string | boolean;
  otherCashBuyers: string | boolean;
}

interface ComparisonChartProps {
  className?: string;
}

const ComparisonChart = ({ className = '' }: ComparisonChartProps) => {
  const comparisonData: ComparisonItem[] = [
    {
      feature: 'Timeline to Close',
      silkSolutions: '30 days or less',
      traditional: '60-90+ days',
      otherCashBuyers: '14-30 days',
    },
    {
      feature: 'Fees & Commissions',
      silkSolutions: 'None',
      traditional: '6-10% of sale price',
      otherCashBuyers: 'Varies',
    },
    {
      feature: 'Repairs Required',
      silkSolutions: false,
      traditional: true,
      otherCashBuyers: 'Sometimes',
    },
    {
      feature: 'Showings & Open Houses',
      silkSolutions: false,
      traditional: true,
      otherCashBuyers: false,
    },
    {
      feature: 'Financing Contingencies',
      silkSolutions: false,
      traditional: true,
      otherCashBuyers: false,
    },
    {
      feature: 'Professional Evaluation',
      silkSolutions: true,
      traditional: true,
      otherCashBuyers: 'Basic',
    },
    {
      feature: 'Transparent Process',
      silkSolutions: true,
      traditional: 'Varies',
      otherCashBuyers: 'Limited',
    },
    {
      feature: 'Local Market Expertise',
      silkSolutions: true,
      traditional: true,
      otherCashBuyers: 'Limited',
    },
    {
      feature: 'Personalized Service',
      silkSolutions: true,
      traditional: 'Varies',
      otherCashBuyers: false,
    },
    {
      feature: 'Fair Market Pricing',
      silkSolutions: true,
      traditional: true,
      otherCashBuyers: 'Varies',
    },
  ];

  const renderValue = (value: string | boolean, isHighlight: boolean = false) => {
    if (typeof value === 'boolean') {
      return (
        <div className="flex justify-center">
          {value ? (
            <Icon
              name="CheckCircleIcon"
              size={20}
              className={isHighlight ? 'text-success' : 'text-success/70'}
            />
          ) : (
            <Icon name="XCircleIcon" size={20} className="text-error/70" />
          )}
        </div>
      );
    }

    return (
      <span
        className={`text-sm ${isHighlight ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}
      >
        {value}
      </span>
    );
  };

  return (
    <section className={`py-16 lg:py-24 bg-background ${className}`}>
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-6">
            Why Choose Silk Solutions?
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Compare our approach to traditional real estate sales and other cash buyers. See why
            we're the preferred choice for sellers who value transparency, speed, and fair
            treatment.
          </p>
        </div>

        {/* Comparison Table */}
        <div className="bg-card rounded-xl shadow-card border border-border overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-4 bg-surface border-b border-border">
            <div className="p-6">
              <h3 className="font-semibold text-foreground">Features</h3>
            </div>
            <div className="p-6 bg-primary/5 border-l border-border">
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Icon name="StarIcon" size={24} className="text-primary" variant="solid" />
                </div>
                <h3 className="font-bold text-primary">Silk Solutions</h3>
              </div>
            </div>
            <div className="p-6 border-l border-border">
              <div className="text-center">
                <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-2">
                  <Icon name="HomeIcon" size={24} className="text-muted-foreground" />
                </div>
                <h3 className="font-semibold text-foreground">Traditional Sale</h3>
              </div>
            </div>
            <div className="p-6 border-l border-border">
              <div className="text-center">
                <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-2">
                  <Icon name="CurrencyDollarIcon" size={24} className="text-muted-foreground" />
                </div>
                <h3 className="font-semibold text-foreground">Other Cash Buyers</h3>
              </div>
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-border">
            {comparisonData.map((item, index) => (
              <div key={index} className="grid grid-cols-4">
                <div className="p-6">
                  <span className="font-medium text-foreground">{item.feature}</span>
                </div>
                <div className="p-6 bg-primary/5 border-l border-border text-center">
                  {renderValue(item.silkSolutions, true)}
                </div>
                <div className="p-6 border-l border-border text-center">
                  {renderValue(item.traditional)}
                </div>
                <div className="p-6 border-l border-border text-center">
                  {renderValue(item.otherCashBuyers)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Key Benefits */}
        <div className="grid md:grid-cols-3 gap-8 mt-16">
          <div className="text-center p-6 bg-card rounded-xl border border-border shadow-card">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="BoltIcon" size={32} className="text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              As Little as 30 Days or Less
            </h3>
            <p className="text-sm text-muted-foreground">
              Close in 30 days or less with our streamlined process and cash offers.
            </p>
          </div>

          <div className="text-center p-6 bg-card rounded-xl border border-border shadow-card">
            <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="ShieldCheckIcon" size={32} className="text-accent" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Completely Transparent</h3>
            <p className="text-sm text-muted-foreground">
              Full breakdown of our offer with no hidden fees or surprise deductions.
            </p>
          </div>

          <div className="text-center p-6 bg-card rounded-xl border border-border shadow-card">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="HandRaisedIcon" size={32} className="text-success" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Zero Hassle</h3>
            <p className="text-sm text-muted-foreground">
              No repairs, no showings, no waiting for buyer financing to fall through.
            </p>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="inline-flex items-center px-6 py-3 bg-accent/10 text-accent rounded-full text-sm font-medium mb-6">
            <Icon name="TrophyIcon" size={16} className="mr-2" />
            Rated #1 Cash Home Buyer in the Area
          </div>
          <h3 className="text-2xl font-heading font-bold text-foreground mb-4">
            Experience the Silk Solutions Difference
          </h3>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Don't settle for less when selling your property. Choose the company that puts your
            needs first with transparent pricing, fast closings, and exceptional service.
          </p>
          <a
            href="#property-form"
            className="inline-flex items-center justify-center px-8 py-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 silk-smooth shadow-button text-lg font-semibold"
          >
            <Icon name="RocketLaunchIcon" size={20} className="mr-2" />
            Get Started Today
          </a>
        </div>
      </div>
    </section>
  );
};

export default ComparisonChart;
